import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyForm = {
  plan_code: '',
  plan_name: '',
  description: '',
  is_active: true,
  module_ids: [],
};

function SubscriptionPlanManager({ token }) {
  const { isMobile } = useIsMobile();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra });

  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const loadPlans = async () => {
    const res = await fetch('/api/v1/subscription/admin/plans', { headers: headers() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load plans');
    return Array.isArray(data) ? data : data.plans || [];
  };

  const loadModules = async () => {
    const res = await fetch('/api/v1/subscription/admin/modules', { headers: headers() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load modules');
    return Array.isArray(data) ? data : data.modules || [];
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [p, m] = await Promise.all([loadPlans(), loadModules()]);
      setPlans(p);
      setModules(m);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [token]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, module_ids: [] });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const openEdit = (plan) => {
    setEditingId(plan.id);
    const ids = (plan.modules || []).map((m) => m.id || m.module_id);
    setForm({
      plan_code: plan.plan_code || '',
      plan_name: plan.plan_name || '',
      description: plan.description || '',
      is_active: plan.is_active !== false,
      module_ids: ids,
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const toggleModule = (modId) => {
    setForm((f) => {
      const ids = f.module_ids.includes(modId)
        ? f.module_ids.filter((id) => id !== modId)
        : [...f.module_ids, modId];
      return { ...f, module_ids: ids };
    });
  };

  const savePlan = async () => {
    setError('');
    setSuccess('');
    if (!form.plan_code.trim() || !form.plan_name.trim()) {
      return setError('Plan Code and Name are required.');
    }
    const url = editingId
      ? `/api/v1/subscription/admin/plans/${editingId}`
      : '/api/v1/subscription/admin/plans';
    const method = editingId ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          plan_code: form.plan_code.trim(),
          plan_name: form.plan_name.trim(),
          description: form.description.trim(),
          is_active: form.is_active,
          module_ids: form.module_ids,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSuccess(editingId ? 'Plan updated.' : 'Plan created.');
      cancelForm();
      loadAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* ── Group modules for the checkbox selector ── */
  const grouped = modules.reduce((acc, m) => {
    const g = m.module_group || 'OTHER';
    if (!acc[g]) acc[g] = [];
    acc[g].push(m);
    return acc;
  }, {});

  /* ── Styles ── */
  const thStyle = {
    padding: '0.6rem 0.75rem', textAlign: 'left', position: 'sticky', top: 0,
    background: '#f5f6fa', borderBottom: '2px solid var(--theme-border, #dcdde1)',
    whiteSpace: 'nowrap', fontSize: '0.82rem', fontWeight: 600,
  };
  const tdStyle = { padding: '0.55rem 0.75rem', borderBottom: '1px solid #eef2f7', fontSize: '0.85rem' };
  const btnPrimary = {
    padding: '0.5rem 1.1rem', background: 'var(--theme-primary, #2563eb)', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  };
  const btnSecondary = {
    padding: '0.5rem 1.1rem', background: '#dcdde1', color: '#2f3640',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  };
  const inputStyle = {
    padding: '0.5rem 0.6rem', border: '1px solid var(--theme-border, #ccc)',
    borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { fontWeight: 600, fontSize: '0.8rem', marginBottom: 2, display: 'block', color: '#34495e' };
  const badgeStyle = (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: '0.75rem', fontWeight: 600, background: color, color: '#333', marginRight: 4, marginBottom: 3,
  });

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.8rem' }}>
        <h2 style={{ color: 'var(--theme-primary-dark, #1e3a5f)', margin: 0 }}>Subscription Plans</h2>
        <button style={btnPrimary} onClick={openAdd}>+ Add Plan</button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {/* ── Add/Edit Panel ── */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <h3 style={{ margin: '0 0 0.8rem', color: 'var(--theme-primary-dark, #1e3a5f)' }}>
            {editingId ? 'Edit Plan' : 'New Plan'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Plan Code</label>
              <input style={inputStyle} value={form.plan_code} onChange={(e) => set('plan_code', e.target.value)} placeholder="e.g. STARTER" />
            </div>
            <div>
              <label style={labelStyle}>Plan Name</label>
              <input style={inputStyle} value={form.plan_name} onChange={(e) => set('plan_name', e.target.value)} placeholder="e.g. Starter" />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.85rem' }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                Active
              </label>
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Plan description" />
            </div>
          </div>

          {/* Module selector */}
          <div style={{ marginTop: '1rem' }}>
            <label style={{ ...labelStyle, marginBottom: 8 }}>Included Modules</label>
            {Object.keys(grouped).sort().map((group) => (
              <div key={group} style={{ marginBottom: '0.6rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#636e72', marginBottom: 4, textTransform: 'uppercase' }}>{group}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {grouped[group].map((m) => {
                    const checked = form.module_ids.includes(m.id);
                    return (
                      <label key={m.id} style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                        borderRadius: 6, border: checked ? '2px solid var(--theme-primary, #2563eb)' : '1px solid #dcdde1',
                        background: checked ? '#eef4ff' : '#fff', cursor: 'pointer', fontSize: '0.83rem',
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleModule(m.id)} />
                        {m.module_name}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            {!modules.length && <div style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>No modules available. Create modules first.</div>}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button style={btnPrimary} onClick={savePlan}>{editingId ? 'Update' : 'Create'}</button>
            <button style={btnSecondary} onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Plans Table ── */}
      {loading && <div style={{ color: '#636e72', marginBottom: '0.8rem' }}>Loading...</div>}

      <div className="card" style={{ overflowX: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Plan Code</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Modules</th>
              <th style={thStyle}>Active</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid #eef2f7' }}>
                <td style={tdStyle}><code>{p.plan_code}</code></td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{p.plan_name}</td>
                <td style={{ ...tdStyle, maxWidth: 220, whiteSpace: 'normal' }}>{p.description || '—'}</td>
                <td style={{ ...tdStyle, maxWidth: 300, whiteSpace: 'normal' }}>
                  {(p.modules || []).map((m) => {
                    const grp = m.module_group || 'OTHER';
                    const bg = grp === 'CORE' ? '#dff9e8' : grp === 'AI' ? '#e8e0ff' : grp === 'FNB' ? '#fff4d6' : '#e0f0ff';
                    return <span key={m.id || m.module_id} style={badgeStyle(bg)}>{m.module_name || m.module_key}</span>;
                  })}
                  {!(p.modules || []).length && <span style={{ color: '#95a5a6' }}>None</span>}
                </td>
                <td style={tdStyle}>
                  <span style={{ color: p.is_active !== false ? '#1f8f4d' : '#c23616', fontWeight: 600 }}>
                    {p.is_active !== false ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button style={{ ...btnSecondary, padding: '0.3rem 0.7rem', fontSize: '0.8rem' }} onClick={() => openEdit(p)}>Edit</button>
                </td>
              </tr>
            ))}
            {!plans.length && !loading && (
              <tr><td colSpan={6} style={{ padding: '1rem', color: '#7f8c8d', textAlign: 'center' }}>No plans found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SubscriptionPlanManager;
